import environment from 'platform/utilities/environment';
import { apiRequest } from 'platform/utilities/api';
import { createApi } from '@reduxjs/toolkit/query/react';
import { setLowAuthFormData, invalidateSession } from '../slices/formSlice';
import { setVassToken, getVassToken, removeVassToken } from '../../utils/auth';
import { createInvalidTokenError } from '../../services/mocks/utils/errors';
import { SERVER_ERROR_CODES } from '../../utils/constants';
import { isSessionAuthError } from '../../utils/errors';
import { normalizeAppointmentTopics } from '../../utils/appointments';

const api = async (url, options, ...rest) => {
  try {
    return await apiRequest(`${environment.API_URL}${url}`, options, ...rest);
  } catch (error) {
    if (error?.errors) {
      throw error;
    }
    const normalized = new Error(
      error?.error || error?.message || 'An unknown error occurred',
    );
    normalized.errors = [
      {
        code: SERVER_ERROR_CODES.SERVICE_ERROR,
        detail: error?.error || error?.message || 'An unknown error occurred',
        status: error?.status,
      },
    ];
    throw normalized;
  }
};

/**
 * Gets the VASS token from the cookie and returns it if it exists.
 * @returns {Object} An object with the token and error if it exists.
 */
const getTokenOrError = () => {
  const token = getVassToken();
  if (!token) {
    return {
      token: null,
      error: createInvalidTokenError().errors[0],
    };
  }
  return { token, error: null };
};

/**
 * Passes through an authenticated-endpoint error; on a session-auth error also
 * clears the token cookie and flags the Redux session as invalidated.
 */
const authenticatedEndpointError = (apiError, dispatch) => {
  if (isSessionAuthError(apiError)) {
    removeVassToken();
    dispatch(invalidateSession());
  }
  return apiError;
};

export const vassApi = createApi({
  reducerPath: 'vassApi',
  baseQuery: () => ({ data: null }),
  keepUnusedDataFor: environment.isUnitTest() ? 0 : 60,
  endpoints: builder => ({
    postAuthentication: builder.mutation({
      async queryFn(
        { uuid, lastName, dob, veteranContactEmail, invitationData },
        { dispatch },
      ) {
        try {
          const body = { uuid, lastName, dob };
          if (veteranContactEmail) {
            body.veteranContactEmail = veteranContactEmail;
          }
          if (invitationData) {
            body.data = invitationData;
          }
          const response = await api('/vass/v0/request-otp', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
          });
          dispatch(
            setLowAuthFormData({
              uuid,
              lastName,
              dob,
              veteranContactEmail,
              invitationData,
              obfuscatedEmail: response.data.email,
            }),
          );
          return response;
        } catch ({ errors }) {
          return {
            error: {
              code: errors?.[0]?.code,
              detail: errors?.[0]?.detail,
            },
          };
        }
      },
    }),
    postOTPVerification: builder.mutation({
      async queryFn({ otp }, { getState }) {
        const { uuid, lastName, dob } = getState().vassForm;
        try {
          return await api('/vass/v0/authenticate-otp', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              otp,
              uuid,
              lastName,
              dob,
            }),
          });
        } catch ({ errors }) {
          return {
            error: errors?.[0],
          };
        }
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.token) {
            setVassToken(data.token);
          }
        } catch {
          // Error is handled by the queryFn
        }
      },
    }),
    postAppointment: builder.mutation({
      async queryFn(
        { topics, dtStartUtc, dtEndUtc, veteranTimeZone },
        { dispatch },
      ) {
        const { token, error: tokenError } = getTokenOrError();
        if (tokenError) {
          return { error: tokenError };
        }
        try {
          return await api('/vass/v0/appointment', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              topics,
              dtStartUtc,
              dtEndUtc,
              veteranTimeZone,
            }),
          });
        } catch ({ errors }) {
          return {
            error: authenticatedEndpointError(
              { code: errors?.[0]?.code, detail: errors?.[0]?.detail },
              dispatch,
            ),
          };
        }
      },
    }),
    getAppointment: builder.query({
      async queryFn({ appointmentId }, { dispatch }) {
        const { token, error: tokenError } = getTokenOrError();
        if (tokenError) {
          return { error: tokenError };
        }
        try {
          const response = await api(`/vass/v0/appointment/${appointmentId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });
          // Normalize the appointment topics from the `skillId`/`skillName` format
          // to the canonical `topicId`/`topicName` format. When the backend migrates
          // to `topicId`/`topicName`, this can be removed.
          return {
            ...response,
            data: normalizeAppointmentTopics(response.data),
          };
        } catch ({ errors }) {
          return {
            error: authenticatedEndpointError(
              { code: errors?.[0]?.code, detail: errors?.[0]?.detail },
              dispatch,
            ),
          };
        }
      },
    }),
    getTopics: builder.query({
      async queryFn(_, { dispatch }) {
        const { token, error: tokenError } = getTokenOrError();
        if (tokenError) {
          return { error: tokenError };
        }
        try {
          return await api('/vass/v0/topics', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });
        } catch ({ errors }) {
          return {
            error: authenticatedEndpointError(
              { code: errors?.[0]?.code, detail: errors?.[0]?.detail },
              dispatch,
            ),
          };
        }
      },
    }),
    getAppointmentAvailability: builder.query({
      async queryFn(_, { dispatch }) {
        const { token, error: tokenError } = getTokenOrError();
        if (tokenError) {
          return { error: tokenError };
        }
        try {
          return await api('/vass/v0/appointment-availability', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });
        } catch ({ errors }) {
          return {
            error: authenticatedEndpointError(errors?.[0], dispatch),
          };
        }
      },
    }),
    cancelAppointment: builder.mutation({
      async queryFn({ appointmentId }, { dispatch }) {
        const { token, error: tokenError } = getTokenOrError();
        if (tokenError) {
          return { error: tokenError };
        }
        try {
          return await api(`/vass/v0/appointment/${appointmentId}/cancel`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });
        } catch ({ errors }) {
          return {
            error: authenticatedEndpointError(
              { code: errors?.[0]?.code, detail: errors?.[0]?.detail },
              dispatch,
            ),
          };
        }
      },
    }),
  }),
});

export const {
  usePostAuthenticationMutation,
  usePostOTPVerificationMutation,
  usePostAppointmentMutation,
  useGetAppointmentQuery,
  useGetTopicsQuery,
  useGetAppointmentAvailabilityQuery,
  useLazyGetAppointmentAvailabilityQuery,
  useCancelAppointmentMutation,
} = vassApi;
